import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../Shared/FormInput";
import FormAlert from "../Shared/FormAlert";
import { useAuthContext } from "../AuthContext";
import cognitoAuthService from "../../../services/cognitoAuthService";
import "../../../styles/auth.css";

const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const { setCurrentView } = useAuthContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const handleForgotPassword = async () => {
    if (!email) {
      setError(t('forgotPassword.pleaseEnterEmail'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.forgotPassword(email);
      setResetCodeSent(true);
      setSuccess(t('forgotPassword.resetCodeSent'));
    } catch (err) {
      setError(err.message || t('forgotPassword.failedToSendResetCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPassword = async () => {
    if (!code || !newPassword) {
      setError(t('forgotPassword.pleaseEnterCodeAndPassword'));
      return;
    }

    try {
      setLoading(true);
      setError("");
      await cognitoAuthService.confirmForgotPassword(email, code, newPassword);
      setSuccess(t('forgotPassword.passwordResetSuccess'));
      setTimeout(() => {
        setCurrentView("login");
      }, 2000);
    } catch (err) {
      setError(err.message || t('forgotPassword.passwordResetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h3>{t('forgotPassword.title')}</h3>
      <FormAlert type="error" message={error} />
      <FormAlert type="success" message={success} />

      {!resetCodeSent && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleForgotPassword();
          }}
        >
          <FormInput
            id="reset-email"
            value={email}
            type="email"
            label={t('forgotPassword.emailLabel')}
            placeholder={t('forgotPassword.emailPlaceholder')}
            disabled={loading}
            onChange={setEmail}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('forgotPassword.sendResetCodeButton')}</span>
            )}
          </button>
        </form>
      )}

      {resetCodeSent && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirmPassword();
          }}
        >
          <p className="info-text">
            {t('forgotPassword.resetCodeInfo')}
          </p>
          <FormInput
            id="reset-code"
            value={code}
            type="text"
            label={t('forgotPassword.resetCodeLabel')}
            placeholder={t('forgotPassword.resetCodePlaceholder')}
            disabled={loading}
            onChange={setCode}
          />
          <FormInput
            id="new-password"
            value={newPassword}
            type="password"
            label={t('forgotPassword.newPasswordLabel')}
            placeholder={t('forgotPassword.newPasswordPlaceholder')}
            disabled={loading}
            onChange={setNewPassword}
          />
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              <span>{t('forgotPassword.resetPasswordButton')}</span>
            )}
          </button>
        </form>
      )}

      <div className="form-footer">
        <button
          type="button"
          onClick={() => setCurrentView("login")}
          className="link"
        >
          {t('forgotPassword.backToSignIn')}
        </button>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
